// ReferralNetwork.tsx

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import { Share2, CheckCircle2, Copy } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import type { ReferralLogItem } from "@/types/BillingAndRewards.dto.types";

interface ReferralNetworkProps {
  referralCode: string;
  history: ReferralLogItem[];
}

export const ReferralNetwork: React.FC<ReferralNetworkProps> = ({ referralCode, history }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const PREFIX = "components.referralNetwork";

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusChip = (status: ReferralLogItem["status"]) => {
    const configs = {
      PENDING_SIGNUP: {
        label: t(`${PREFIX}.status.pendingPurchase`),
        color: "warning" as const,
      },
      QUALIFIED: {
        label: t(`${PREFIX}.status.qualified`),
        color: "info" as const,
      },
      REWARD_DISTRIBUTED: {
        label: t(`${PREFIX}.status.distributed`),
        color: "success" as const,
      },
      CANCELLED_FRAUD: {
        label: t(`${PREFIX}.status.flaggedVoid`),
        color: "error" as const,
      },
    };

    const currentConfig = configs[status as keyof typeof configs] || {
      label: status || t(`${PREFIX}.status.unknown`),
      color: "default" as const,
    };

    return <Chip label={currentConfig.label} color={currentConfig.color} size="small" />;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Share Code Engine Widget */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          bgcolor: "background.paper",
        }}
      >
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
            {t(`${PREFIX}.inviteTitle`)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            <Trans i18nKey={`${PREFIX}.inviteDescription`} components={{ strong: <strong /> }} />
          </Typography>
          <Box sx={{ display: "flex", gap: 2, maxWidth: 500 }}>
            <TextField
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Share2 size={16} />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
              value={referralCode}
            />
            <Button
              variant="contained"
              onClick={handleCopyCode}
              color={copied ? "success" : "primary"}
              startIcon={copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
              sx={{ px: 3, minWidth: 130 }}
            >
              {copied ? t(`${PREFIX}.copied`) : t(`${PREFIX}.copyLink`)}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Data Grid Logs Ledger */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
            {t(`${PREFIX}.ledgerTitle`)}
          </Typography>
        </Box>
        <Table>
          <TableHead sx={{ bgcolor: "action.hover" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                {t(`${PREFIX}.tableHeaders.invitedColleague`)}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                {t(`${PREFIX}.tableHeaders.dateRegistered`)}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                {t(`${PREFIX}.tableHeaders.statusCondition`)}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "right", color: "text.primary" }}>
                {t(`${PREFIX}.tableHeaders.accruedReward`)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {row.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "text.primary" }}>{row.date}</TableCell>
                <TableCell>{getStatusChip(row.status)}</TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    textAlign: "right",
                    color: row.status === "REWARD_DISTRIBUTED" ? "success.main" : "text.primary",
                  }}
                >
                  {row.reward}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
